import React from 'react';
import { useFileViewerContext } from 'js/file_viewer/contexts/file_viewer';
import { filePreviewSessionIdAtom } from 'js/file_viewer/core/atoms/ui/logging';
import { useSetFileViewerAtom } from 'js/file_viewer/core/atoms/use_atom';
import { isNestedArchiveFilePreviewKey } from 'js/file_viewer/core/data/preview_key';
import { BackgroundEvent, SessionEventType, SourceContext, } from 'js/file_viewer/core/logging/constants';
import { getLoggingSession, initializeLoggingSession, recordEvent, serializeSession, setLoggingSession, updateSessionLastEvent, } from 'js/file_viewer/core/logging/session';
import { getWhitelistedFileExtension } from 'js/file_viewer/core/utils/extension_constants';
import { getFileExtension } from 'js/file_viewer/core/utils/paths';
import { resolvePreviewType } from 'js/file_viewer/core/utils/support';
import { useStabilizedCallback } from 'js/file_viewer/core/utils/useStabilizedCallback';
import { v4 } from 'js/file_viewer/core/utils/uuid';
import { PreviewsEventLogger } from 'js/file_viewer/logging/performance';
export const useLogSessionEvent = () => {
    const { logger } = useFileViewerContext();
    return useStabilizedCallback((previewSessionId, event, tags) => {
        const session = getLoggingSession(previewSessionId);
        if (!session) {
            return;
        }
        const updatedSession = recordEvent(session, event);
        if (updatedSession) {
            logger === null || logger === void 0 ? void 0 : logger.log(event, serializeSession(updatedSession, tags), tags);
            setLoggingSession(previewSessionId, updateSessionLastEvent(updatedSession, event));
        }
        else {
            const updatedTags = {
                ...tags,
                current_event: event,
            };
            logger === null || logger === void 0 ? void 0 : logger.log(BackgroundEvent.InvalidLoggingOrder, serializeSession(session, updatedTags), updatedTags);
        }
    });
};
const useLifecycleLogging = (previewSessionId, attemptEvent, successEvent, hasSucceeded) => {
    const logSessionEvent = useLogSessionEvent();
    React.useMemo(() => {
        if (previewSessionId && attemptEvent) {
            logSessionEvent(previewSessionId, attemptEvent);
        }
    }, [attemptEvent, logSessionEvent, previewSessionId]);
    React.useMemo(() => {
        if (previewSessionId && hasSucceeded) {
            logSessionEvent(previewSessionId, successEvent);
        }
    }, [hasSucceeded, logSessionEvent, previewSessionId, successEvent]);
};
export const useSupportConfirmed = (previewSessionId, hasSucceeded) => {
    useLifecycleLogging(previewSessionId, SessionEventType.FilePreviewAttemptStarted, SessionEventType.FilePreviewSupportConfirmed, hasSucceeded);
};
export const useDownloadSucceeded = (previewSessionId, hasSucceeded) => {
    useLifecycleLogging(previewSessionId, SessionEventType.FilePreviewDownloadAttempted, SessionEventType.FilePreviewDownloadSucceeded, hasSucceeded);
};
export const useRenderSucceededWithLogging = (previewSessionId, onRenderSucceeded) => {
    const [hasRendered, setHasRendered] = React.useState(false);
    useLifecycleLogging(previewSessionId, undefined, SessionEventType.FilePreviewRenderSucceeded, hasRendered);
    return React.useCallback(() => {
        setHasRendered(true);
        onRenderSucceeded();
    }, [onRenderSucceeded]);
};
export const useInitializePreviewSession = (previewKey, rootFileInfo, rootFileMetadata, onPreviewSessionInitialized, previewMetadata) => {
    const { deviceType, loggingSessionId, featureConfig, sourceSession } = useFileViewerContext();
    // HACK ALERT: This change instantly when flipping but the store state lags
    // a bit in being updated to the new preview.  Having these in the deps of
    // the useMemo means we will initialize a preview session for the old preview
    // with the new source context and feature config before the store updates.
    // Hopefully we can remove this if we ever figure out how redux works.
    // TODO: (htwyford) Is this still necessary now that our store is in Jotai?
    const currentFeatureConfig = React.useRef(featureConfig);
    currentFeatureConfig.current = featureConfig;
    const currentSourceSession = React.useRef(sourceSession);
    currentSourceSession.current = sourceSession;
    const currentRootFileInfo = React.useRef(rootFileInfo);
    currentRootFileInfo.current = rootFileInfo;
    const currentRootFileMetadata = React.useRef(rootFileMetadata);
    currentRootFileMetadata.current = rootFileMetadata;
    const filePreviewSessionId = React.useMemo(() => {
        const newPreviewSessionId = v4();
        const fileMetadata = currentRootFileMetadata.current;
        const fileInfo = currentRootFileInfo.current;
        if (!previewKey || !fileInfo || !fileMetadata) {
            return undefined;
        }
        const isArchive = isNestedArchiveFilePreviewKey(previewKey);
        const fileExtension = getFileExtension(isArchive ? previewKey.subpath : fileMetadata.file_name);
        const session = isArchive
            ? {
                source_context: SourceContext.ArchiveFile,
                page_start_ts: Date.now(),
                was_flipped: false,
            }
            : currentSourceSession.current;
        const previewType = resolvePreviewType(previewMetadata, isArchive ? previewKey.subpath : fileMetadata.file_name);
        initializeLoggingSession(newPreviewSessionId, {
            file_viewer_session_id: loggingSessionId,
            file_preview_session_id: newPreviewSessionId,
            file_id: fileInfo.file_id,
            sj_id: fileInfo.sj_id,
            ns_id: fileInfo.ns_id,
            device_type: deviceType,
            file_extension: getWhitelistedFileExtension(fileExtension),
            file_preview_size: fileMetadata.size,
            extra: {
                ...session,
                preview_type: previewType,
            },
        });
        onPreviewSessionInitialized === null || onPreviewSessionInitialized === void 0 ? void 0 : onPreviewSessionInitialized(newPreviewSessionId);
        return newPreviewSessionId;
    }, [deviceType, loggingSessionId, onPreviewSessionInitialized, previewKey]);
    const setFilePreviewSessionId = useSetFileViewerAtom(filePreviewSessionIdAtom);
    React.useEffect(() => {
        // React does not like it when you update state in a `useMemo`
        setFilePreviewSessionId(filePreviewSessionId);
    }, [filePreviewSessionId, setFilePreviewSessionId]);
    return filePreviewSessionId;
};
export const useEndSession = (previewSessionId) => {
    const logSessionEvent = useLogSessionEvent();
    React.useEffect(() => {
        if (!previewSessionId) {
            return;
        }
        const endSession = () => {
            var _a;
            const lastEvent = (_a = getLoggingSession(previewSessionId)) === null || _a === void 0 ? void 0 : _a.extra.last_event;
            logSessionEvent(previewSessionId, SessionEventType.FilePreviewSessionEnded, {
                ...(lastEvent && { last_event: lastEvent }),
            });
            PreviewsEventLogger.endCurrentSession();
        };
        window.addEventListener('beforeunload', endSession);
        return () => {
            endSession();
            window.removeEventListener('beforeunload', endSession);
        };
    }, [logSessionEvent, previewSessionId]);
};
//# sourceMappingURL=lifecycle_logging.preenvesnextjs.map
